import { Check, Eye, EyeOff } from "lucide-react";
import { useState } from "react";

import { Switch } from "@/components/ui/Form.jsx";
import Tag from "@/components/ui/Tag.jsx";
import { cx } from "@/lib/cx.js";

/* ---- In-page section nav ------------------------------------------------ */

/*
 * The sticky contents list. Plain anchors to the section ids — CSS
 * `scroll-mt-20` on each section keeps the heading clear of the topbar, so
 * there is no scroll maths here and no JS scroll handler to fight the browser.
 */
export function SectionNav({ sections, active }) {
    return (
        <nav className="hidden lg:block" aria-label="Settings sections">
            <div className="sticky top-20 space-y-0.5">
                {sections.map((s) => (
                    <a
                        key={s.id}
                        href={`#sec-${s.id}`}
                        data-section-link={s.id}
                        aria-current={active === s.id ? "true" : undefined}
                        className={cx(
                            "block rounded-md px-3 py-2 text-[13px] font-medium transition",
                            active === s.id
                                ? "bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-200"
                                : "text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white",
                        )}
                    >
                        {s.label}
                    </a>
                ))}
            </div>
        </nav>
    );
}

/* ---- Toggle row --------------------------------------------------------- */

/*
 * A locked row stays visible and disabled with the reason, rather than being
 * hidden: an admin who has read the plan's feature list needs to see that the
 * switch exists and why they cannot use it.
 */
export function ToggleRow({ field, checked, locked, onChange }) {
    return (
        <div
            data-toggle={field.key}
            className={cx(
                "flex items-center justify-between gap-4 rounded-lg border px-3.5 py-3 transition",
                locked
                    ? "border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/40"
                    : "border-slate-200 dark:border-slate-800",
            )}
        >
            <span className="min-w-0">
                <span className="flex flex-wrap items-center gap-2">
                    <span className="text-[13px] font-medium">{field.label}</span>
                    {locked && <Tag tone="amber">Not available</Tag>}
                </span>
                <span className="mt-0.5 block text-[11.5px] text-slate-400 dark:text-slate-500">
                    {locked ? field.gateNote : field.hint}
                </span>
            </span>

            <Switch
                checked={locked ? false : checked}
                disabled={locked}
                onChange={(e) => onChange(e.target.checked)}
                aria-label={field.label}
            />
        </div>
    );
}

/* ---- Radio-card picker -------------------------------------------------- */

export function PickerRow({ options, value, onChange, name }) {
    return (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {options.map((option) => {
                const active = option.key === value;
                return (
                    <button
                        key={option.key}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        data-pick={`${name}:${option.key}`}
                        onClick={() => onChange(option.key)}
                        className={cx(
                            "flex items-start gap-2.5 rounded-lg border p-3.5 text-left transition",
                            active
                                ? "border-brand-500 bg-brand-50/60 ring-2 ring-brand-500/20 dark:border-brand-600 dark:bg-brand-950/40"
                                : "border-slate-200 hover:border-slate-300 dark:border-slate-800 dark:hover:border-slate-700",
                        )}
                    >
                        <span
                            className={cx(
                                "mt-0.5 grid size-4 shrink-0 place-items-center rounded-full border-2",
                                active
                                    ? "border-brand-600 bg-brand-600 text-white dark:border-brand-500 dark:bg-brand-500"
                                    : "border-slate-300 dark:border-slate-600",
                            )}
                        >
                            {active && <Check className="size-2.5" strokeWidth={3.5} />}
                        </span>
                        <span className="min-w-0">
                            <span className="block text-[13px] font-medium">{option.label}</span>
                            <span className="mt-0.5 block text-[11.5px] text-slate-400 dark:text-slate-500">
                                {option.hint}
                            </span>
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

/* ---- Password field with a reveal --------------------------------------- */

/*
 * These two are desktop GATE passwords, not credentials — the model stores them
 * in plain text on purpose, and an admin has to be able to read back what they
 * set in order to tell someone. Hence a reveal button rather than the
 * write-only treatment a real secret would get.
 */
export function RevealField({ id, value, onChange, placeholder, autoComplete = "off" }) {
    const [shown, setShown] = useState(false);

    return (
        <div className="relative">
            <input
                id={id}
                data-field={id}
                type={shown ? "text" : "password"}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                autoComplete={autoComplete}
                className="field-lg pr-11 font-mono"
            />
            <button
                type="button"
                onClick={() => setShown((v) => !v)}
                aria-label={shown ? "Hide" : "Show"}
                aria-pressed={shown}
                data-reveal={id}
                className="absolute top-1/2 right-2.5 grid size-7 -translate-y-1/2 place-items-center rounded text-slate-400 transition hover:bg-slate-100 dark:hover:bg-slate-800"
            >
                {shown ? (
                    <EyeOff className="size-4.5" strokeWidth={1.8} />
                ) : (
                    <Eye className="size-4.5" strokeWidth={1.8} />
                )}
            </button>
        </div>
    );
}
